// The Cost Report's By cost type view as an Excel workbook: cost and
// revenue with the math for every line, so each number on the view can be
// checked in the file (Seth 10/27: "Export that puppy to Excel … Details AND
// Overview with the math for EACH transaction line item").
//
//   Overview — area → cost type → detail, the view's own tree, the detail
//              rows folded under +/-. Every figure is a SUMIFS over Detail.
//   Detail   — one row per cost line, then one per invoice adjustment. Each
//              line works its own revenue in formulas: what it bills at
//              (hours × rate, or cost × (1 + markup)), the fuel surcharge on
//              top, whether it is billable, which sent invoice claimed it,
//              invoiced or still to invoice, and the margin.
//   Invoices — each sent invoice's cover, charge by charge, against the
//              lines it claimed. The gap is the "invoiced above / below"
//              row on Detail.
//
// The rules are billingProfit.js's; this file only lays them out. Where a
// line's bill can't be re-worked from what the line carries (a rate frozen
// on a summary since changed), the bill is written as a value and its
// Pricing says so — a formula that didn't reproduce the bill would put a
// number in the file the view doesn't have.
//
// Internal: it carries margin, so it says so on its face and in its name.

import moment from 'moment';
import { pNum } from '../db/company';
import { AREAS, billsLine, cantInvoice, lineArea, lineDetail, profitTree } from './billingProfit';
import { LABOR_BILLABLE_CT } from './billingPricing';
import { fuelLineAmt } from './billingFuel';
import { clientEquipLabel } from './billingEquipNames';
import { XLSX_STYLE, costReportBuffer } from './billingCostReportXlsx';

const {
    NAVY, NAVY_LIGHT, WASH, WASH_SOFT, HAIR, INK, MUTED, BILL_INK, NB_INK, NB_WASH, INTERNAL,
    MONEY, HOURS, COUNT, setBorder, fill,
} = XLSX_STYLE;
const LOSS = 'FFD13138';
const PCT = '0.0%;-0.0%;"-"';
const PCT_RATE = '0.00##%';
const RATE = '#,##0.00##';

const r2 = (n) => Math.round(pNum(n) * 100) / 100;
const AREA_LABEL = {
    ...Object.fromEntries(AREAS.map((a) => [a.k, a.label])),
    credit: 'Credits on invoices', other: 'Other invoice charges', mob: 'Mobilization', tax: 'Sales tax',
};
const CHARGE_LABEL = {
    labor: 'Labor', perdiem: 'Per diem', equipment: 'Equipment + fuel', thirdparty: 'Third party + handling',
    tax: 'Sales tax', mob: 'Mobilization', credit: 'Credit', other: 'Other',
};

// Spreadsheet column letter for a 1-based index.
const colL = (n) => {
    let s = '';
    for (let x = n; x > 0; x = Math.floor((x - 1) / 26)) s = String.fromCharCode(65 + ((x - 1) % 26)) + s;
    return s;
};

// The Detail sheet's columns. `fmt` picks the number format; the formulas
// are written by the builder, which knows each column's letter.
export const DETAIL_COLS = [
    { k: 'type', h: 'Row Type', w: 16 },
    { k: 'area', h: 'Area', w: 13 },
    { k: 'ct', h: 'Cost Type', w: 10 },
    { k: 'ctDesc', h: 'Cost Type Description', w: 26 },
    { k: 'detail', h: 'Detail', w: 30 },
    { k: 'cc', h: 'Cost Code', w: 11 },
    { k: 'ccDesc', h: 'Cost Code Description', w: 28 },
    { k: 'date', h: 'Work Date', w: 11 },
    { k: 'who', h: 'Employee / Vendor', w: 24 },
    { k: 'eq', h: 'Equipment', w: 26 },
    { k: 'po', h: 'PO', w: 10 },
    { k: 'vinv', h: 'Vendor Invoice #', w: 15 },
    { k: 'ds', h: 'Description', w: 36 },
    { k: 'hrs', h: 'Hours', w: 9, fmt: HOURS },
    { k: 'cost', h: 'Cost', w: 14, fmt: MONEY },
    { k: 'bills', h: 'Billable?', w: 9 },
    { k: 'cant', h: "Can't Invoice?", w: 10 },
    { k: 'why', h: 'Why Not', w: 40 },
    { k: 'cantAmt', h: "Can't Invoice $", w: 14, fmt: MONEY },
    { k: 'basis', h: 'Pricing', w: 34 },
    { k: 'wage', h: 'Wage', w: 10, fmt: RATE },
    { k: 'rate', h: 'Rate / Hr', w: 11, fmt: RATE },
    { k: 'mk', h: 'Markup', w: 9, fmt: PCT_RATE },
    { k: 'bill', h: 'Bill', w: 14, fmt: MONEY },
    { k: 'fuelPct', h: 'Fuel %', w: 8, fmt: PCT_RATE },
    { k: 'fuel', h: 'Fuel Surcharge', w: 13, fmt: MONEY },
    { k: 'lineRev', h: 'Line Revenue', w: 14, fmt: MONEY },
    { k: 'invNo', h: 'Invoice # (claimed)', w: 15 },
    { k: 'sumNo', h: 'Fusion Summary #', w: 15 },
    { k: 'inv', h: 'Invoiced', w: 14, fmt: MONEY },
    { k: 'open', h: 'To Be Invoiced', w: 14, fmt: MONEY },
    { k: 'rev', h: 'Revenue', w: 14, fmt: MONEY },
    { k: 'margin', h: 'Margin', w: 14, fmt: MONEY },
    { k: 'pct', h: 'Margin %', w: 9, fmt: PCT },
    { k: 'areaKey', h: 'Area Key', w: 10 },
    { k: 'typeKey', h: 'Type Key', w: 9 },
    { k: 'detailKey', h: 'Detail Key', w: 10 },
];
const D = Object.fromEntries(DETAIL_COLS.map((c, i) => [c.k, colL(i + 1)]));

// Why a line doesn't bill, in words. Blank on a line that bills.
export const whyNot = (t) => {
    if (billsLine(t)) return '';
    if (t.neverBilled) return 'Never billed: burden, payroll taxes, truck / rig pay';
    if (t.nb) return t.nbReason || 'Held off the invoice';
    if (t.cls === 'Labor' && !LABOR_BILLABLE_CT.includes(String(t.ct || ''))) return 'Labor cost type that does not bill (only 511 / 512 / 515)';
    if (t.noRate) return 'No rate yet — map the classification on Rates & Rules';
    return 'Does not bill';
};

/**
 * How a billable line's bill is worked, so the Detail row can carry it as a
 * formula: { how: 'hours' | 'markup' | 'value', rate, mk, basis }. 'hours'
 * and 'markup' are used only when they reproduce the line's bill to the
 * cent; anything else is written as the value it bills at.
 */
export const billMath = (t) => {
    const bill = pNum(t.bill);
    const frozen = t.inv ? ` — frozen on ${t.inv}` : '';
    const hrs = pNum(t.hrs);
    if (t.hourly && hrs && pNum(t.rateAmt)) {
        const perHr = t.rateUom === 'hr' ? Number(t.rateAmt) : Number(t.rateAmt) / 10;
        if (Math.abs(r2(hrs * perHr) - bill) < 0.005) {
            const basis = t.cls === 'Labor'
                ? `Hours × rate (wage × multiplier${t.ct === '51200' ? ' × OT factor' : ''})`
                : `Hours × rate sheet rate${t.ba ? ` (${t.ba})` : ''}${t.rateUom === 'hr' ? '' : `, ${t.rateUom || 'day'} rate ÷ 10`}`;
            return { how: 'hours', rate: perHr, basis: basis + frozen };
        }
    }
    if (!t.hourly) {
        const mk = pNum(t.mk);
        if (Math.abs(pNum(t.cost) * (1 + mk / 100) - bill) < 0.005) {
            const basis = !mk ? (t.pt ? 'Pass-through at cost' : 'At cost')
                : t.pt ? 'Cost × (1 + pass-through %)' : 'Cost × (1 + markup)';
            return { how: 'markup', mk: mk / 100, basis: basis + frozen };
        }
    }
    return { how: 'value', basis: `As billed${t.inv ? ` on ${t.inv}` : ''}` };
};

const keyMaker = (prefix) => {
    const ids = {};
    let n = 0;
    return (k) => ids[k] || (ids[k] = `${prefix}${(n += 1)}`);
};

/**
 * The workbook. `txns` is the whole job (the Cost Report's reportTxns),
 * `claims` invoiceClaims' result for it, `fuelCtx` the Billing tab's fuel
 * index. Returns the ExcelJS workbook.
 */
export const buildProfitWorkbook = async (txns, claims, fuelCtx, meta = {}) => {
    const ExcelJS = (await import('exceljs')).default;
    const wb = new ExcelJS.Workbook();
    wb.creator = 'Fusion Billing';
    wb.created = new Date();
    // ExcelJS writes no cached result for a falsy one; the file works its
    // formulas on open so nothing reads blank.
    wb.calcProperties = { fullCalcOnLoad: true };

    const lines = txns || [];
    const tree = profitTree(lines, claims, { withAdj: true });
    const typeId = keyMaker('T');
    const detailId = keyMaker('D');
    const jobKey = meta.jobKey || '';
    const provenance = [
        jobKey ? `Job ${jobKey}` : null, meta.jobName || null, meta.client || null,
        `Prepared ${moment().format('MMMM D, YYYY')}`, meta.userName ? `by ${meta.userName}` : null,
    ].filter(Boolean).join('   ·   ');

    // Sheets in reading order; Detail and Invoices are filled first so the
    // Overview's formulas can point at their ranges.
    const wsO = wb.addWorksheet('Overview', { views: [{ showGridLines: false }] });
    const wsD = wb.addWorksheet('Detail', { views: [{ state: 'frozen', ySplit: 1, xSplit: 5 }] });
    const wsI = wb.addWorksheet('Invoices', { views: [{ state: 'frozen', ySplit: 1 }] });

    const header = (ws, heads) => {
        const h = ws.addRow(heads);
        h.height = 30;
        h.eachCell((c) => {
            fill(c, NAVY);
            c.font = { bold: true, size: 10, color: { argb: 'FFFFFFFF' } };
            c.alignment = { vertical: 'middle', wrapText: true };
        });
        return h;
    };

    // ── Invoices: each cover charge against the lines it claimed ─────────
    const I_COLS = [
        { k: 'no', h: 'Invoice #', w: 13 }, { k: 'status', h: 'Status', w: 10 }, { k: 'replaces', h: 'Replaces', w: 12 },
        { k: 'from', h: 'Period From', w: 11 }, { k: 'to', h: 'Period To', w: 11 }, { k: 'period', h: 'Has Period?', w: 9 },
        { k: 'charge', h: 'Charge', w: 22 }, { k: 'areaKey', h: 'Area Key', w: 10 },
        { k: 'billed', h: 'Billed on Cover', w: 15, fmt: MONEY }, { k: 'lines', h: 'Lines Claimed', w: 9, fmt: COUNT },
        { k: 'claimed', h: 'Claimed Lines Revenue', w: 15, fmt: MONEY }, { k: 'diff', h: 'Difference', w: 14, fmt: MONEY },
        { k: 'total', h: 'Invoice Total', w: 14, fmt: MONEY }, { k: 'due', h: 'Amount Due', w: 14, fmt: MONEY },
    ];
    const I = Object.fromEntries(I_COLS.map((c, i) => [c.k, colL(i + 1)]));
    wsI.columns = I_COLS.map((c) => ({ width: c.w }));
    header(wsI, I_COLS.map((c) => c.h));
    const invByNo = Object.fromEntries((claims.invs || []).map((r) => [r.no, r]));
    const ties = claims.ties || [];
    // Detail's last data row isn't known yet; its ranges run to a fixed
    // bottom set once the lines are counted.
    const nCost = lines.length;
    const lineAreas = AREAS.map((a) => a.k);
    const iFirst = 2;
    const iLast = iFirst + ties.length + (claims.unread || []).length - 1;
    // Detail rows: the cost lines, then the adjustment rows (at most a few
    // per area, plus credits, other charges and mobilization).
    const adjCount = lineAreas.reduce((a, k) => a + (claims.adj[k] ? 2 : 0), 0) + 2
        + ((claims.mob && claims.mob.invs.length) || 0) + ((claims.mob && claims.mob.applied.length) || 0);
    const dLast = 1 + nCost + adjCount;
    const dr = (k) => `Detail!$${D[k]}$2:$${D[k]}$${dLast}`;
    const ir = (k) => `Invoices!$${I[k]}$${iFirst}:$${I[k]}$${Math.max(iFirst, iLast)}`;

    ties.forEach((t) => {
        const r = invByNo[t.no] || {};
        const hasPeriod = !!(t.from && t.to);
        const tied = lineAreas.includes(t.area);
        const row = wsI.addRow(I_COLS.map(() => null));
        const n = row.number;
        const set = (k, v) => { row.getCell(I_COLS.findIndex((c) => c.k === k) + 1).value = v; };
        set('no', t.no); set('status', r.status || ''); set('replaces', r.replaces || '');
        set('from', t.from || ''); set('to', t.to || ''); set('period', hasPeriod ? 'Y' : 'N');
        set('charge', CHARGE_LABEL[t.area] || t.area); set('areaKey', t.area); set('billed', r2(t.billed));
        if (tied && hasPeriod) {
            set('lines', { formula: `COUNTIFS(${dr('invNo')},${I.no}${n},${dr('areaKey')},${I.areaKey}${n})`, result: t.lines });
            set('claimed', { formula: `SUMIFS(${dr('lineRev')},${dr('invNo')},${I.no}${n},${dr('areaKey')},${I.areaKey}${n})`, result: r2(t.priced) });
            set('diff', { formula: `${I.billed}${n}-${I.claimed}${n}`, result: r2(t.billed - t.priced) });
        }
        if (r.total != null) set('total', r2(r.total));
        if (r.amountDue != null) set('due', r2(r.amountDue));
        I_COLS.forEach((c, i) => {
            const cell = row.getCell(i + 1);
            if (c.fmt) cell.numFmt = c.fmt;
            cell.font = { size: 10, color: { argb: INK } };
            setBorder(cell, ['bottom']);
        });
    });
    (claims.unread || []).forEach((no) => {
        const r = invByNo[no] || {};
        const row = wsI.addRow([no, r.status || '', r.replaces || '', r.from || '', r.to || '', '', 'Cover not read in DocuSign yet — not counted']);
        row.font = { size: 10, italic: true, color: { argb: MUTED } };
    });
    if (wsI.rowCount > 1) wsI.autoFilter = { from: { row: 1, column: 1 }, to: { row: 1, column: I_COLS.length } };

    // ── Detail: one row per cost line, then the invoice adjustments ──────
    wsD.columns = DETAIL_COLS.map((c) => ({ width: c.w }));
    header(wsD, DETAIL_COLS.map((c) => c.h));
    const C = (k) => DETAIL_COLS.findIndex((c) => c.k === k) + 1;
    const paint = (row, i, wash) => {
        DETAIL_COLS.forEach((c, idx) => {
            const cell = row.getCell(idx + 1);
            if (c.fmt) cell.numFmt = c.fmt;
            cell.font = { size: 10, color: { argb: c.k.endsWith('Key') ? MUTED : INK } };
            setBorder(cell, ['bottom']);
            if (wash) fill(cell, wash);
            else if (i % 2 === 1) fill(cell, WASH_SOFT);
        });
    };
    const sumOf = { cost: 0, cant: 0, lineRev: 0, rev: 0, inv: 0, open: 0, hrs: 0 };

    // Areas first, then cost type, detail and date — the Overview's order.
    const areaOrder = Object.fromEntries(AREAS.map((a, i) => [a.k, i]));
    const ordered = lines.slice().sort((a, b) => (areaOrder[lineArea(a)] - areaOrder[lineArea(b)])
        || String(a.ct || '').localeCompare(String(b.ct || ''))
        || lineDetail(a).localeCompare(lineDetail(b))
        || String(a.date || '').localeCompare(String(b.date || '')));

    ordered.forEach((t, i) => {
        const area = lineArea(t);
        const tk = `${area}|${t.ct || '(no cost type)'}`;
        const bills = billsLine(t);
        const cant = cantInvoice(t);
        const cost = pNum(t.cost);
        const math = bills ? billMath(t) : null;
        const bill = bills ? pNum(t.bill) : 0;
        const fuel = bills ? fuelLineAmt(t, fuelCtx || {}) : 0;
        const lineRev = bills ? bill + fuel : 0;
        const invNo = bills ? claims.lineInv[t.key] || '' : '';
        const inv = invNo ? lineRev : 0;
        const open = lineRev - inv;
        const row = wsD.addRow(DETAIL_COLS.map(() => null));
        const n = row.number;
        const f = (formula, result) => ({ formula, result });
        const v = {
            type: t.rentUsage ? 'Rental usage' : 'Cost line',
            area: AREA_LABEL[area],
            ct: t.ct || '', ctDesc: t.ctDesc || '', detail: lineDetail(t),
            cc: t.cc || '', ccDesc: t.ccDesc || '',
            date: t.date ? moment(t.date).format('M/D/YYYY') : '',
            who: t.who || '',
            eq: t.cls === 'Equipment' || t.unit ? clientEquipLabel({ ...t, who: '' }) : '',
            po: t.po || '', vinv: t.vinvNo || '', ds: t.ds || '',
            hrs: pNum(t.hrs) || null, cost: r2(cost),
            bills: bills ? 'Y' : 'N', cant: cant ? 'Y' : 'N', why: whyNot(t),
            cantAmt: f(`IF(${D.cant}${n}="Y",${D.cost}${n},0)`, cant ? r2(cost) : 0),
            basis: math ? math.basis : '',
            wage: t.cls === 'Labor' && pNum(t.wage) ? Number(t.wage) : null,
            rate: math && math.how === 'hours' ? math.rate : null,
            mk: math && math.how === 'markup' ? math.mk : null,
            bill: !math ? null
                : math.how === 'hours' ? f(`ROUND(${D.hrs}${n}*${D.rate}${n},2)`, r2(bill))
                : math.how === 'markup' ? f(`${D.cost}${n}*(1+${D.mk}${n})`, bill)
                : bill,
            fuelPct: fuel && bill ? fuel / bill : null,
            fuel: fuel && bill ? f(`${D.bill}${n}*${D.fuelPct}${n}`, fuel) : null,
            lineRev: f(`IF(${D.bills}${n}="Y",N(${D.bill}${n})+N(${D.fuel}${n}),0)`, lineRev),
            invNo, sumNo: t.inv || '',
            inv: f(`IF(${D.invNo}${n}<>"",${D.lineRev}${n},0)`, inv),
            open: f(`${D.lineRev}${n}-${D.inv}${n}`, open),
            rev: f(`${D.inv}${n}+${D.open}${n}`, lineRev),
            margin: f(`${D.rev}${n}-${D.cost}${n}`, lineRev - cost),
            pct: f(`IF(${D.rev}${n}=0,"",${D.margin}${n}/${D.rev}${n})`, lineRev ? (lineRev - cost) / lineRev : ''),
            areaKey: area, typeKey: typeId(tk), detailKey: detailId(`${tk}|${lineDetail(t)}`),
        };
        DETAIL_COLS.forEach((c, idx) => { row.getCell(idx + 1).value = v[c.k]; });
        paint(row, i, cant ? NB_WASH : null);
        row.getCell(C('bills')).font = { size: 10, bold: true, color: { argb: bills ? BILL_INK : NB_INK } };
        if (lineRev - cost < 0) row.getCell(C('margin')).font = { size: 10, color: { argb: LOSS } };
        sumOf.cost += cost; sumOf.hrs += pNum(t.hrs); sumOf.cant += cant ? cost : 0;
        sumOf.lineRev += lineRev; sumOf.rev += lineRev; sumOf.inv += inv; sumOf.open += open;
    });

    // The invoice adjustments: what the covers billed beyond the lines they
    // claimed, credits, other charges and the mobilization — each worked
    // off the Invoices sheet where it can be.
    let adjI = 0;
    const adjRow = (o) => {
        const row = wsD.addRow(DETAIL_COLS.map(() => null));
        const n = row.number;
        const v = {
            type: 'Invoice adjustment', area: AREA_LABEL[o.area], ct: '', detail: o.label, ds: o.note,
            inv: o.inv, open: o.open == null ? 0 : o.open,
            rev: { formula: `${D.inv}${n}+${D.open}${n}`, result: o.result.inv + (o.result.open || 0) },
            margin: { formula: `${D.rev}${n}-N(${D.cost}${n})`, result: o.result.inv + (o.result.open || 0) },
            areaKey: o.area, typeKey: o.typeKey ? typeId(o.typeKey) : '',
        };
        DETAIL_COLS.forEach((c, idx) => { row.getCell(idx + 1).value = v[c.k] == null ? null : v[c.k]; });
        paint(row, 0, WASH);
        row.getCell(C('detail')).font = { size: 10, italic: true, color: { argb: INK } };
        sumOf.inv += o.result.inv; sumOf.open += o.result.open || 0; sumOf.rev += o.result.inv + (o.result.open || 0);
        adjI += 1;
        return n;
    };
    lineAreas.forEach((k) => {
        const a = claims.adj[k];
        if (!a) return;
        if (Math.abs(a.reprice) >= 0.005) {
            adjRow({
                area: k, typeKey: `${k}|adj-reprice`, label: 'Invoiced above / below these lines\' pricing',
                note: `What ${a.invs.join(', ')} billed for these dates beyond what Fusion prices the same lines at (actual wage, credits baked in, rate fixes) — the Difference column on Invoices`,
                inv: { formula: `SUMIFS(${ir('diff')},${ir('areaKey')},"${k}",${ir('period')},"Y")`, result: a.reprice },
                result: { inv: a.reprice },
            });
        }
        if (Math.abs(a.unmatched) >= 0.005) {
            const n = wsD.rowCount + 1;
            adjRow({
                area: k, typeKey: `${k}|adj-unmatched`, label: 'Invoiced, not tied to cost lines',
                note: `${a.invs.join(', ')} bills no work period, so its amount comes off To Be Invoiced as a whole rather than line by line`,
                inv: { formula: `SUMIFS(${ir('billed')},${ir('areaKey')},"${k}",${ir('period')},"N")`, result: a.unmatched },
                open: { formula: `-${D.inv}${n}`, result: -a.unmatched },
                result: { inv: a.unmatched, open: -a.unmatched },
            });
        }
    });
    [['credit', 'Credits on invoices'], ['other', 'Other invoice charges']].forEach(([k, label]) => {
        const amt = claims.extras[k];
        if (Math.abs(amt) < 0.005) return;
        adjRow({
            area: k, label, note: 'On the invoice covers with no cost line behind it',
            inv: { formula: `SUMIFS(${ir('billed')},${ir('areaKey')},"${k}")`, result: amt }, result: { inv: amt },
        });
    });
    if (claims.mob) {
        claims.mob.invs.forEach((m) => {
            const n = wsD.rowCount + 1;
            adjRow({
                area: 'mob', typeKey: `mob|mob-${m.no}`, label: `${m.no} — mobilization`,
                note: 'An advance later invoices apply against, so it comes off To Be Invoiced and is not revenue',
                inv: { formula: `SUMIFS(${ir('billed')},${ir('no')},"${m.no}",${ir('areaKey')},"mob")`, result: m.amt },
                open: { formula: `-${D.inv}${n}`, result: -m.amt },
                result: { inv: m.amt, open: -m.amt },
            });
        });
        claims.mob.applied.forEach((a) => {
            const r = invByNo[a.no] || {};
            const n = wsD.rowCount + 1;
            adjRow({
                area: 'mob', typeKey: `mob|mobapp-${a.no}`, label: `Applied on ${a.no}`,
                note: `${a.no}'s work is invoiced in its own areas above and was paid from the mobilization: invoice total ${r2(r.total)} − amount due ${r2(r.amountDue)}`,
                inv: -r2(a.amt), open: { formula: `-${D.inv}${n}`, result: a.amt },
                result: { inv: -a.amt, open: a.amt },
            });
        });
    }
    // Blank rows up to the range bottom the formulas were written against.
    for (let i = adjI; i < adjCount; i += 1) wsD.addRow([]);

    const totD = wsD.addRow(DETAIL_COLS.map(() => null));
    totD.getCell(1).value = 'TOTAL';
    ['hrs', 'cost', 'cantAmt', 'lineRev', 'inv', 'open', 'rev', 'margin'].forEach((k) => {
        const res = { hrs: sumOf.hrs, cost: sumOf.cost, cantAmt: sumOf.cant, lineRev: sumOf.lineRev, inv: sumOf.inv, open: sumOf.open, rev: sumOf.rev, margin: sumOf.rev - sumOf.cost }[k];
        totD.getCell(C(k)).value = { formula: `SUM(${D[k]}2:${D[k]}${dLast})`, result: res };
    });
    totD.getCell(C('pct')).value = { formula: `IF(${D.rev}${totD.number}=0,"",${D.margin}${totD.number}/${D.rev}${totD.number})`, result: sumOf.rev ? (sumOf.rev - sumOf.cost) / sumOf.rev : '' };
    DETAIL_COLS.forEach((c, idx) => {
        const cell = totD.getCell(idx + 1);
        if (c.fmt) cell.numFmt = c.fmt;
        cell.font = { bold: true, size: 10, color: { argb: INK } };
        setBorder(cell, ['top'], NAVY);
    });
    wsD.autoFilter = { from: { row: 1, column: 1 }, to: { row: 1, column: DETAIL_COLS.length } };

    // ── Overview: the view's tree, every figure a SUMIFS over Detail ─────
    const O_COLS = [
        { k: 'label', h: 'Area / Cost Type / Detail', w: 56 },
        { k: 'lines', h: 'Lines', w: 8, fmt: COUNT },
        { k: 'hrs', h: 'Hours', w: 11, fmt: HOURS },
        { k: 'cost', h: 'Cost', w: 16, fmt: MONEY },
        { k: 'cant', h: "Can't Invoice", w: 16, fmt: MONEY },
        { k: 'inv', h: 'Invoiced', w: 16, fmt: MONEY },
        { k: 'open', h: 'To Be Invoiced', w: 16, fmt: MONEY },
        { k: 'revenue', h: 'Revenue', w: 16, fmt: MONEY },
        { k: 'margin', h: 'Margin', w: 16, fmt: MONEY },
        { k: 'pct', h: 'Margin %', w: 10, fmt: PCT },
    ];
    const LAST = O_COLS.length;
    const O = Object.fromEntries(O_COLS.map((c, i) => [c.k, colL(i + 1)]));
    wsO.columns = O_COLS.map((c) => ({ width: c.w }));
    wsO.properties.outlineProperties = { summaryBelow: false, summaryRight: false };
    wsO.pageSetup = { orientation: 'landscape', fitToPage: true, fitToWidth: 1, fitToHeight: 0 };

    const t1 = wsO.addRow([`${jobKey ? jobKey + ' — ' : ''}COST & REVENUE`]);
    t1.height = 26;
    t1.getCell(1).font = { bold: true, size: 18, color: { argb: INTERNAL } };
    wsO.mergeCells(t1.number, 1, t1.number, LAST);
    const t2 = wsO.addRow(['Cost, what can\'t be invoiced, revenue invoiced and still to invoice, and the margin — by area, cost type and detail. Every line\'s math is on Detail; every invoice\'s tie-out on Invoices.']);
    t2.getCell(1).font = { size: 10.5, color: { argb: MUTED } };
    wsO.mergeCells(t2.number, 1, t2.number, LAST);
    const t3 = wsO.addRow([provenance]);
    t3.getCell(1).font = { size: 9.5, italic: true, color: { argb: MUTED } };
    wsO.mergeCells(t3.number, 1, t3.number, LAST);
    const t4 = wsO.addRow(['INTERNAL — NOT FOR CLIENT DISTRIBUTION. Shows cost and margin.']);
    t4.height = 18;
    for (let i = 1; i <= LAST; i += 1) {
        fill(t4.getCell(i), NB_WASH);
        t4.getCell(i).font = { bold: true, size: 10, color: { argb: INTERNAL } };
    }
    wsO.mergeCells(t4.number, 1, t4.number, LAST);
    wsO.addRow([]);

    const oh = wsO.addRow(O_COLS.map((c) => c.h));
    oh.height = 28;
    oh.eachCell((c) => {
        fill(c, NAVY);
        c.font = { bold: true, size: 11, color: { argb: 'FFFFFFFF' } };
        c.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
        setBorder(c, ['top', 'left', 'bottom', 'right'], NAVY_LIGHT);
    });
    oh.getCell(1).alignment = { vertical: 'middle', horizontal: 'left' };
    wsO.views = [{ state: 'frozen', ySplit: oh.number, showGridLines: false }];

    // One Overview row. `by` is the Detail key column it sums on and `key`
    // the value; the row's own Revenue / Margin / % work off its cells.
    const oRow = (node, level, by, key) => {
        const row = wsO.addRow(O_COLS.map(() => null));
        const n = row.number;
        const crit = `${dr(by)},"${key}"`;
        const s = (k) => `SUMIFS(${dr(k)},${crit})`;
        row.getCell(1).value = node.label;
        row.getCell(1).alignment = { indent: level * 2 };
        row.getCell(2).value = { formula: `COUNTIFS(${crit},${dr('type')},"<>Invoice adjustment")`, result: node.lines };
        row.getCell(3).value = { formula: s('hrs'), result: node.hrs };
        row.getCell(4).value = { formula: s('cost'), result: node.cost };
        row.getCell(5).value = { formula: s('cantAmt'), result: node.cant };
        row.getCell(6).value = { formula: s('inv'), result: node.inv };
        row.getCell(7).value = { formula: s('open'), result: node.open };
        row.getCell(8).value = { formula: `${O.inv}${n}+${O.open}${n}`, result: node.inv + node.open };
        row.getCell(9).value = { formula: `${O.revenue}${n}-${O.cost}${n}`, result: node.inv + node.open - node.cost };
        if (!node.adj) row.getCell(10).value = { formula: `IF(${O.revenue}${n}=0,"",${O.margin}${n}/${O.revenue}${n})`, result: node.pct == null ? '' : node.pct };
        O_COLS.forEach((c, i) => {
            const cell = row.getCell(i + 1);
            if (c.fmt) cell.numFmt = c.fmt;
            cell.font = {
                size: level ? 10 : 11, bold: level === 0, italic: !!node.adj && level > 0,
                color: { argb: (c.k === 'margin' || c.k === 'pct') && node.margin < 0 ? LOSS : level === 2 ? MUTED : INK },
            };
            if (level === 0) fill(cell, WASH);
            setBorder(cell, ['bottom'], level === 0 ? NAVY_LIGHT : HAIR);
        });
        if (level) row.outlineLevel = level;
        // Opens to the cost types; the people, units and vendors fold away.
        if (level === 2) row.hidden = true;
        return row;
    };
    tree.groups.forEach((g) => {
        oRow(g, 0, 'areaKey', g.key);
        g.kids.forEach((r) => {
            oRow(r, 1, 'typeKey', typeId(`${g.key}|${r.key}`));
            (r.kids || []).forEach((x) => oRow(x, 2, 'detailKey', detailId(`${g.key}|${r.key}|${x.key}`)));
        });
    });
    const tot = wsO.addRow(O_COLS.map(() => null));
    const tn = tot.number;
    const T = tree.total;
    tot.getCell(1).value = 'TOTAL';
    tot.getCell(2).value = { formula: `COUNTIFS(${dr('type')},"<>Invoice adjustment",${dr('areaKey')},"<>")`, result: T.lines };
    tot.getCell(3).value = { formula: `SUM(${dr('hrs')})`, result: T.hrs };
    tot.getCell(4).value = { formula: `SUM(${dr('cost')})`, result: T.cost };
    tot.getCell(5).value = { formula: `SUM(${dr('cantAmt')})`, result: T.cant };
    tot.getCell(6).value = { formula: `SUM(${dr('inv')})`, result: T.inv };
    tot.getCell(7).value = { formula: `SUM(${dr('open')})`, result: T.open };
    tot.getCell(8).value = { formula: `${O.inv}${tn}+${O.open}${tn}`, result: T.revenue };
    tot.getCell(9).value = { formula: `${O.revenue}${tn}-${O.cost}${tn}`, result: T.margin };
    tot.getCell(10).value = { formula: `IF(${O.revenue}${tn}=0,"",${O.margin}${tn}/${O.revenue}${tn})`, result: T.pct == null ? '' : T.pct };
    O_COLS.forEach((c, i) => {
        const cell = tot.getCell(i + 1);
        if (c.fmt) cell.numFmt = c.fmt;
        fill(cell, NAVY);
        cell.font = { bold: true, size: 11, color: { argb: 'FFFFFFFF' } };
    });

    // How to read it, and what is on the invoices but isn't revenue.
    wsO.addRow([]);
    const notes = [
        'Cost — every cost line, as JDE posted it.',
        "Can't Invoice — cost that never bills: burden and payroll taxes, lines held off the invoice, labor cost outside 511 / 512 / 515.",
        'Invoiced — revenue on invoices sent in DocuSign (out for approval or approved), fuel surcharge included. A line is invoiced when its work date falls in an invoice\'s period for its area (Detail: Invoice # (claimed)).',
        'To Be Invoiced — billable lines no sent invoice covers yet, at their bill amount with fuel surcharge.',
        'Revenue — Invoiced + To Be Invoiced.   Margin — Revenue − Cost.   Margin % — Margin ÷ Revenue.',
        'Each line\'s bill on Detail: labor ST / OT is hours × (wage × multiplier, × OT factor on OT); rate-sheet equipment is hours × the rate sheet rate (a day rate ÷ 10); everything else is cost × (1 + markup). Per diem passes through at cost.',
        'Click the + beside a cost type for its people, units and vendors.',
    ];
    if (Math.abs(claims.extras.tax || 0) >= 0.005) notes.push(`Sales tax ${r2(claims.extras.tax).toLocaleString('en-US', { style: 'currency', currency: 'USD' })} is also on the invoices — collected for the state, so not revenue.`);
    if ((claims.unread || []).length) notes.push(`Not counted yet: ${claims.unread.join(', ')} (cover not read in DocuSign yet).`);
    notes.forEach((s) => {
        const r = wsO.addRow([s]);
        r.getCell(1).font = { size: 10, color: { argb: MUTED } };
        r.getCell(1).alignment = { wrapText: true, vertical: 'top' };
        wsO.mergeCells(r.number, 1, r.number, LAST);
        r.height = s.length > 150 ? 28 : 15;
    });

    return wb;
};

/** Build and hand the file to the browser. Returns its name. */
export const downloadProfitReport = async (txns, claims, fuelCtx, meta = {}) => {
    const wb = await buildProfitWorkbook(txns, claims, fuelCtx, meta);
    const buffer = await costReportBuffer(wb);
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    const name = `Cost_and_Revenue_INTERNAL_${meta.jobKey || 'job'}_${moment().format('YYYY-MM-DD')}.xlsx`;
    a.href = url;
    a.download = name;
    a.click();
    URL.revokeObjectURL(url);
    return name;
};
